
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type User = {
  id: string;
  email?: string;
  name?: string;
  agency_name?: string;
  avatar_url?: string;
};

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        
        // Get session
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setUser(null);
          return;
        }
        
        // Get user profile data
        const { data, error } = await supabase
          .from('users')
          .select('id, email, name, agency_name, avatar_url')
          .eq('id', session.user.id)
          .single();
          
        if (error) {
          console.error('Error fetching user data:', error);
          // If we can't fetch user profile, at least use auth data
          setUser({
            id: session.user.id,
            email: session.user.email,
            name: session.user.user_metadata?.name,
            agency_name: session.user.user_metadata?.agency_name
          });
        } else {
          setUser(data);
        }
      } catch (error) {
        console.error('Auth error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
    
    // Setup subscription for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        fetchUser();
      } else if (event === 'SIGNED_OUT') {
        setUser(null);
      }
    });
    
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  
  return { user, loading };
}
